import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  getLiveAccessApplicationReviewDetails,
  reviewLiveAccessApplication,
  sendLiveAccessNotification,
  suspendLiveAccess,
  blockLiveAccess,
  liftLiveBlock,
  liftLiveSuspension,
  LiveAccessApplicationReviewDetails,
  LiveAccessApplicationStatus,
  LiveEligibilityCondition,
} from '../services/liveAccessApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusBadgeClass = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  if (status === 'BLOCKED') return 'bg-orange-100 text-orange-700 border-orange-200';
  if (status === 'SUSPENDED') return 'bg-red-100 text-red-800 border-red-300';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const renderRequirementValue = (condition: LiveEligibilityCondition) => {
  if (typeof condition.current === 'number' && typeof condition.required === 'number') {
    return `${condition.current} / ${condition.required}`;
  }
  return condition.statusLabel || (condition.met ? 'Done' : 'Missing');
};

const StatCard: React.FC<{ label: string; value: string | number; accent?: string }> = ({
  label,
  value,
  accent,
}) => (
  <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-center'>
    <p className={`text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</p>
    <p className='mt-1 text-xs text-gray-500 font-medium uppercase tracking-wide'>{label}</p>
  </div>
);

const NOTIFICATION_DEFAULTS: Record<string, { title: string; body: string }> = {
  APPROVED: {
    title: "You're approved to go live!",
    body: "Great news! Your application to go live on PingTop has been approved. Open the app and tap Go Live to start streaming now.",
  },
  REJECTED: {
    title: 'Live access application update',
    body: "Unfortunately, your application to go live has not been approved at this time. Please review the eligibility requirements and reapply once you meet them.",
  },
  PENDING: {
    title: 'Your application is under review',
    body: "We've received your live access application and it's currently being reviewed. We'll notify you as soon as a decision has been made.",
  },
};

const LiveAccessUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const REJECTION_REASONS = [
    'Active Plus subscription required',
    'Trial subscription — upgrade to full Plus first',
    'Insufficient followers (minimum 100 required)',
    'Minimum 3 videos required on your profile',
    'Minimum 3 completed referral rewards required',
    'Account not verified',
    'Active policy violations on account',
    'Account status not active',
    'Content does not meet community guidelines',
    'Other',
  ];

  const [details, setDetails] = useState<LiveAccessApplicationReviewDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [decisionLoading, setDecisionLoading] = useState('');
  const [rejectPreset, setRejectPreset] = useState('');
  const [rejectCustom, setRejectCustom] = useState('');
  const [blockNote, setBlockNote] = useState('');
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifLoading, setNotifLoading] = useState(false);

  const rejectNote = rejectPreset === 'Other' ? rejectCustom : rejectPreset;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getLiveAccessApplicationReviewDetails(userId);
      setDetails(data);
      const existingNote = data.application?.Note || '';
      // Pre-select dropdown if note matches a preset
      if (existingNote && REJECTION_REASONS.includes(existingNote)) {
        setRejectPreset(existingNote);
        setRejectCustom('');
      } else if (existingNote) {
        setRejectPreset('Other');
        setRejectCustom(existingNote);
      }
      // Pre-fill notification fields based on current application status
      const appStatus = data.application?.Status ?? 'PENDING';
      const defaults = NOTIFICATION_DEFAULTS[appStatus] ?? NOTIFICATION_DEFAULTS['PENDING'];
      setNotifTitle(defaults.title);
      setNotifBody(defaults.body);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load application details');
    } finally {
      setLoading(false);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const handleSendNotification = async () => {
    if (!userId) return;
    if (!notifTitle.trim() || !notifBody.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setNotifLoading(true);
    try {
      await sendLiveAccessNotification(userId, { title: notifTitle.trim(), body: notifBody.trim() });
      toast.success('Notification sent');
      setNotifTitle('');
      setNotifBody('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send notification');
    } finally {
      setNotifLoading(false);
    }
  };

  const handleReview = async (status: LiveAccessApplicationStatus) => {
    if (!userId) return;
    if (status === 'REJECTED' && !rejectNote.trim()) {
      toast.error('A decline reason is required before rejecting');
      return;
    }
    setDecisionLoading(status);
    try {
      await reviewLiveAccessApplication({
        userId,
        status,
        ...(status === 'REJECTED' ? { note: rejectNote.trim() } : {}),
      });
      toast.success(`Application ${status.toLowerCase()} successfully`);
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to review application');
    } finally {
      setDecisionLoading('');
    }
  };

  const handleSuspend = async () => {
    if (!userId) return;
    if (!window.confirm('Permanently suspend this user\'s live access? This will terminate any active session immediately. This action cannot be undone.')) return;
    setDecisionLoading('SUSPENDED');
    try {
      await suspendLiveAccess(userId);
      toast.success('User suspended from live access');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to suspend');
    } finally {
      setDecisionLoading('');
    }
  };

  const handleBlock = async () => {
    if (!userId) return;
    if (!blockNote.trim()) {
      toast.error('A reason is required to block');
      return;
    }
    setDecisionLoading('BLOCKED');
    try {
      await blockLiveAccess(userId, blockNote.trim());
      toast.success('User blocked from live access');
      setBlockNote('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to block');
    } finally {
      setDecisionLoading('');
    }
  };

  const handleLiftBlock = async () => {
    if (!userId) return;
    setDecisionLoading('LIFT_BLOCK');
    try {
      await liftLiveBlock(userId);
      toast.success('Block lifted — application returned to Pending');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to lift block');
    } finally {
      setDecisionLoading('');
    }
  };

  const handleLiftSuspension = async () => {
    if (!userId) return;
    if (!window.confirm('Lift this suspension? The application will return to Pending and the user can reapply.')) return;
    setDecisionLoading('LIFT_SUSPENSION');
    try {
      await liftLiveSuspension(userId);
      toast.success('Suspension lifted — application returned to Pending');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to lift suspension');
    } finally {
      setDecisionLoading('');
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500'>Application details not found.</p>
          <button
            onClick={() => navigate('/live-access')}
            className='mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
          >
            ← Back to Live Access
          </button>
        </div>
      </div>
    );
  }

  const { application, profile, eligibility, activePolicyViolations, subscription } = details;
  const status = application?.Status ?? 'PENDING';
  const isPending = status === 'PENDING';
  const isBlocked = status === 'BLOCKED';
  const isSuspended = status === 'SUSPENDED';
  const displayName = profile?.Name || profile?.UserName || 'Unknown User';
  const username = profile?.UserName ? `@${profile.UserName}` : null;
  const targetUserId = userId || profile?.UserID || '';

  // Pull numeric values from eligibility conditions
  const videoCondition = eligibility.conditions.find((c) => c.id === 'upload_video');
  const followerCondition = eligibility.conditions.find((c) => c.id === 'min_followers');
  const referralCondition = eligibility.conditions.find((c) => c.id === 'invite_friends');

  const videoCount = videoCondition?.current ?? 0;
  const followerCount = followerCondition?.current ?? profile?.FollowersCount ?? 0;
  const referralCount = referralCondition?.current ?? 0;

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Back */}
        <button
          onClick={() => navigate('/live-access')}
          className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Live Access
        </button>

        {/* Header card */}
        <div className='mb-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex items-center gap-4'>
              <div className='flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] text-xl font-bold text-white shrink-0'>
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className='text-xl font-bold text-gray-900'>{displayName}</h1>
                {username && <p className='text-sm text-gray-500'>{username}</p>}
                <p className='mt-0.5 text-xs text-gray-400 font-mono'>{userId}</p>
              </div>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${getStatusBadgeClass(status)}`}>
                {status}
              </span>
              <button onClick={() => navigate(`/live-access/streams?hostUserId=${encodeURIComponent(targetUserId)}`)}
                className='rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>
                Livestreams
              </button>
              <button onClick={() => navigate(`/live-access/reports?hostUserId=${encodeURIComponent(targetUserId)}`)}
                className='rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>
                Live Reports
              </button>
              <button onClick={() => navigate(`/live-access/${userId}/profile`)}
                className='rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>
                Full Profile →
              </button>
              <a href={`/bulk-review?userId=${encodeURIComponent(userId || '')}&userName=${encodeURIComponent(profile?.UserName || '')}`}
                target='_blank' rel='noopener noreferrer'
                className='rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>
                Videos ↗
              </a>
            </div>
          </div>
          {application && (
            <div className='mt-3 flex flex-wrap gap-4 border-t border-gray-100 pt-3 text-sm text-gray-600'>
              <span><span className='font-semibold text-gray-800'>Applied:</span> {formatDate(application.AppliedAt)}</span>
              {application.ReviewedAt && <span><span className='font-semibold text-gray-800'>Reviewed:</span> {formatDate(application.ReviewedAt)}</span>}
              {application.Note && <span><span className='font-semibold text-gray-800'>Note:</span> {application.Note}</span>}
            </div>
          )}
        </div>

        {/* Stats strip */}
        <div className='mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4'>
          <StatCard label='Followers' value={followerCount.toLocaleString()} accent='text-blue-600' />
          <StatCard label='Videos' value={videoCount} accent='text-purple-600' />
          <StatCard label='Referrals' value={referralCount} accent='text-emerald-600' />
          <StatCard label='Plus Verified' value={profile?.IsPlusVerified ? 'Yes' : 'No'}
            accent={profile?.IsPlusVerified ? 'text-emerald-600' : 'text-rose-500'} />
        </div>

        {/* ── Decision bar (full-width, always visible) ─────────────────── */}
        <div className='mb-5 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
          <div className='border-b border-gray-200 px-5 py-3 bg-gray-50'>
            <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-600'>Moderation Actions</h2>
          </div>
          <div className='grid gap-0 divide-y divide-gray-100 lg:divide-y-0 lg:divide-x lg:grid-cols-3'>

            {/* Approve / Reject */}
            <div className='p-5'>
              <p className='mb-3 text-sm font-semibold text-gray-800'>Approve / Reject</p>
              {isSuspended ? (
                <p className='text-xs text-gray-400 italic'>Not available while suspended</p>
              ) : (
                <div className='space-y-2'>
                  {!isPending && !isBlocked && (
                    <p className='rounded bg-gray-50 px-2 py-1.5 text-center text-xs text-gray-500'>
                      Currently {status.toLowerCase()}
                    </p>
                  )}
                  <select value={rejectPreset} onChange={(e) => setRejectPreset(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'>
                    <option value=''>— Decline reason —</option>
                    {REJECTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {rejectPreset === 'Other' && (
                    <textarea value={rejectCustom} onChange={(e) => setRejectCustom(e.target.value)}
                      rows={2} placeholder='Describe the reason…'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none' />
                  )}
                  <div className='grid grid-cols-2 gap-2'>
                    <button onClick={() => handleReview('APPROVED')} disabled={Boolean(decisionLoading)}
                      className='rounded-lg bg-emerald-600 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60'>
                      {decisionLoading === 'APPROVED' ? '…' : status === 'APPROVED' ? 'Re-Approve' : 'Approve'}
                    </button>
                    <button onClick={() => handleReview('REJECTED')} disabled={Boolean(decisionLoading)}
                      className='rounded-lg bg-rose-600 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60'>
                      {decisionLoading === 'REJECTED' ? '…' : status === 'REJECTED' ? 'Re-Reject' : 'Reject'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Block Access */}
            <div className='p-5'>
              <p className='mb-1 text-sm font-semibold text-orange-800'>Block Access</p>
              <p className='mb-3 text-xs text-orange-600'>Blocked users cannot reapply. Reapply attempts tracked.</p>
              {isSuspended ? (
                <p className='text-xs text-gray-400 italic'>Not available while suspended</p>
              ) : isBlocked ? (
                <div className='space-y-2'>
                  {(application?.ReapplyCount ?? 0) > 0 && (
                    <p className='text-xs font-semibold text-orange-700'>⚠ Reapply attempts: {application?.ReapplyCount}</p>
                  )}
                  {application?.BlockedAt && <p className='text-xs text-orange-600'>Blocked: {formatDate(application.BlockedAt)}</p>}
                  <button onClick={handleLiftBlock} disabled={Boolean(decisionLoading)}
                    className='w-full rounded-lg bg-orange-600 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60'>
                    {decisionLoading === 'LIFT_BLOCK' ? 'Lifting…' : 'Lift Block → Pending'}
                  </button>
                </div>
              ) : (
                <div className='space-y-2'>
                  <textarea value={blockNote} onChange={(e) => setBlockNote(e.target.value)} rows={2}
                    placeholder='Reason for blocking (required)…'
                    className='w-full rounded-lg border border-orange-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none' />
                  <button onClick={handleBlock} disabled={Boolean(decisionLoading)}
                    className='w-full rounded-lg bg-orange-600 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60'>
                    {decisionLoading === 'BLOCKED' ? 'Blocking…' : 'Block Access'}
                  </button>
                </div>
              )}
            </div>

            {/* Suspend Access */}
            <div className='p-5'>
              <p className='mb-1 text-sm font-semibold text-red-800'>Suspend Access</p>
              <p className='mb-3 text-xs text-red-600'>Terminates any active session immediately.</p>
              {isSuspended ? (
                <div className='space-y-2'>
                  <div className='rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800 space-y-1'>
                    <p className='font-semibold'>Currently suspended</p>
                    {application?.SuspendedAt && <p>Since: {formatDate(application.SuspendedAt)}</p>}
                    {application?.SuspendedBy && <p>By: {application.SuspendedBy}</p>}
                  </div>
                  <button onClick={handleLiftSuspension} disabled={Boolean(decisionLoading)}
                    className='w-full rounded-lg bg-red-700 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60'>
                    {decisionLoading === 'LIFT_SUSPENSION' ? 'Lifting…' : 'Lift Suspension → Pending'}
                  </button>
                </div>
              ) : (
                <button onClick={handleSuspend} disabled={Boolean(decisionLoading)}
                  className='w-full rounded-lg bg-red-700 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60'>
                  {decisionLoading === 'SUSPENDED' ? 'Suspending…' : 'Suspend Permanently'}
                </button>
              )}
            </div>

          </div>
        </div>

        {/* ── Main content + sidebar ─────────────────────────────────────── */}
        <div className='grid gap-5 lg:grid-cols-3'>

          {/* Left: Eligibility + Policy violations + Appeals */}
          <div className='lg:col-span-2 space-y-5'>

            {/* Eligibility */}
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-base font-semibold text-gray-900'>Eligibility</h2>
                <span className={`text-sm font-semibold ${eligibility.canGoLive ? 'text-emerald-600' : 'text-rose-500'}`}>
                  {eligibility.canGoLive ? '✓ Can go live' : '✗ Not eligible'}
                </span>
              </div>
              <div className='mb-2 flex items-center justify-between text-xs text-gray-500'>
                <span>{eligibility.progress.label}</span>
                <span>{eligibility.progress.current} / {eligibility.progress.total}</span>
              </div>
              <div className='h-2 w-full rounded-full bg-gray-100 overflow-hidden'>
                <div className='h-full rounded-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] transition-all'
                  style={{ width: `${Math.min(100, (eligibility.progress.current / Math.max(eligibility.progress.total, 1)) * 100)}%` }} />
              </div>
              <div className='mt-4 grid gap-3 sm:grid-cols-2'>
                {eligibility.conditions.map((condition) => {
                  const isSubCard = condition.id === 'active_subscription';
                  return (
                    <div key={condition.id} className='flex items-start justify-between gap-2 rounded-lg border border-gray-200 px-3 py-3'>
                      <div className='min-w-0'>
                        <p className='text-sm font-semibold text-gray-900 truncate'>{condition.label}</p>
                        <p className='mt-0.5 text-xs text-gray-500 truncate'>{renderRequirementValue(condition)}</p>
                        {isSubCard && subscription && (
                          <div className='mt-1.5 space-y-0.5'>
                            <p className='text-xs text-gray-500'>
                              <span className='font-medium text-gray-700'>{subscription.status || '—'}</span>
                              {subscription.periodType && <span className='ml-1 text-gray-400'>· {subscription.periodType}</span>}
                              {subscription.isTrial && <span className='ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-amber-700 font-semibold'>Trial</span>}
                            </p>
                            {subscription.subscribedAt && <p className='text-xs text-gray-400'>Since {formatDate(subscription.subscribedAt)}</p>}
                            {subscription.expiresAt ? <p className='text-xs text-gray-400'>Expires {formatDate(subscription.expiresAt)}</p> : <p className='text-xs text-gray-400'>No expiry</p>}
                          </div>
                        )}
                      </div>
                      <span className={`shrink-0 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${condition.met ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {condition.met ? 'Met' : 'Missing'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Policy violations */}
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <h2 className='mb-3 text-base font-semibold text-gray-900'>
                Active Policy Violations
                {activePolicyViolations.length > 0 && (
                  <span className='ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-xs font-bold text-white'>
                    {activePolicyViolations.length}
                  </span>
                )}
              </h2>
              {activePolicyViolations.length === 0 ? (
                <p className='text-sm text-gray-500'>No active policy violations.</p>
              ) : (
                <div className='space-y-3'>
                  {activePolicyViolations.map((v) => (
                    <div key={v.ViolationID} className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900'>
                      <p className='font-semibold'>{v.Code}</p>
                      <p className='mt-1'>{v.Reason}</p>
                      <p className='mt-1.5 text-xs text-rose-600'>Source: {v.Source} · Expires: {formatDate(v.ExpiresAt)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Appeals History */}
            {(() => {
              const appeals = application?.Appeals ?? (application?.AppealMessage ? [{ message: application.AppealMessage, screenshotUrls: [], submittedAt: application.AppealSubmittedAt ?? '' }] : []);
              return (
                <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                  <div className='mb-4 flex items-center justify-between'>
                    <h2 className='text-base font-semibold text-gray-900'>
                      Appeal History
                      {appeals.length > 0 && (
                        <span className='ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white'>
                          {appeals.length}
                        </span>
                      )}
                    </h2>
                    {status === 'REJECTED' && eligibility.application.rejectionNote && (
                      <span className='text-xs text-rose-600 font-medium'>Rejection note: {eligibility.application.rejectionNote}</span>
                    )}
                  </div>
                  {appeals.length === 0 ? (
                    <p className='text-sm text-gray-500'>No appeals submitted.</p>
                  ) : (
                    <div className='space-y-4'>
                      {[...appeals].reverse().map((appeal, i) => (
                        <div key={i} className='rounded-lg border border-blue-100 bg-blue-50 p-4'>
                          <div className='flex items-start justify-between gap-3 mb-2'>
                            <span className='text-xs font-semibold text-blue-700 uppercase tracking-wide'>
                              Appeal #{appeals.length - i}
                            </span>
                            <span className='text-xs text-gray-400'>{formatDate(appeal.submittedAt)}</span>
                          </div>
                          <p className='text-sm text-gray-800 whitespace-pre-wrap'>{appeal.message}</p>
                          {appeal.screenshotUrls.length > 0 && (
                            <div className='mt-3'>
                              <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>Screenshots</p>
                              <div className='flex flex-wrap gap-2'>
                                {appeal.screenshotUrls.map((url, j) => (
                                  <a key={j} href={url} target='_blank' rel='noreferrer'
                                    className='group overflow-hidden rounded-lg border border-blue-200 hover:border-[#FF9C15]'>
                                    <img src={url} alt={`Appeal screenshot ${j + 1}`}
                                      className='h-20 w-20 object-cover transition-transform group-hover:scale-105' />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

          </div>

          {/* Right: Utility cards */}
          <div className='space-y-4'>

            {/* Send Notification */}
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <h2 className='mb-4 text-base font-semibold text-gray-900'>Send Notification</h2>
              <div className='space-y-3'>
                <div>
                  <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500'>Title</label>
                  <input type='text' value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)}
                    placeholder='e.g. Your live access application'
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none' />
                </div>
                <div>
                  <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500'>Message</label>
                  <textarea value={notifBody} onChange={(e) => setNotifBody(e.target.value)} rows={3}
                    placeholder='Write your message…'
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none' />
                </div>
                <button onClick={handleSendNotification} disabled={notifLoading}
                  className='w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60'>
                  {notifLoading ? 'Sending…' : 'Send Notification'}
                </button>
              </div>
            </div>

            {/* Profile summary */}
            {profile && (
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <h2 className='mb-3 text-base font-semibold text-gray-900'>Profile Summary</h2>
                <div className='space-y-2 text-sm'>
                  {[
                    ['Account Status', profile.AccountStatus || 'N/A'],
                    ['Plus Verified', profile.IsPlusVerified ? 'Yes' : 'No'],
                    ['Active Strikes', String(profile.activePolicyViolationCount ?? 0)],
                  ].map(([label, val]) => (
                    <div key={label} className='flex justify-between'>
                      <span className='text-gray-500'>{label}</span>
                      <span className='font-semibold text-gray-800'>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription */}
            {subscription && (
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <h2 className='mb-3 text-base font-semibold text-gray-900'>Subscription</h2>
                <div className='space-y-2 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>Status</span>
                    <span className={`font-semibold ${subscription.isTrial ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {subscription.status || 'N/A'}
                      {subscription.isTrial && <span className='ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700'>Trial</span>}
                    </span>
                  </div>
                  {subscription.periodType && (
                    <div className='flex justify-between'>
                      <span className='text-gray-500'>Period</span>
                      <span className='font-semibold text-gray-800'>{subscription.periodType}</span>
                    </div>
                  )}
                  {subscription.subscribedAt && (
                    <div className='flex justify-between'>
                      <span className='text-gray-500'>Subscribed</span>
                      <span className='font-semibold text-gray-800'>{formatDate(subscription.subscribedAt)}</span>
                    </div>
                  )}
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>Expires</span>
                    <span className='font-semibold text-gray-800'>{subscription.expiresAt ? formatDate(subscription.expiresAt) : 'No expiry'}</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAccessUserDetail;
